import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  PHONE_1,
  PHONE_2,
  ACCOUNT_NUMBER,
  BANK_NAME,
  CURRENCY,
  LEASE_DUE_DATE,
  MONTHLY_DUE_DATE,
  ROUTING_NUMBER,
  YEARLY_DUE_DATE,
} from "./consts";
import {
  useGetCommunityFinancialInfo,
  useUpdateFinancialInfoMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { routes } from "@/routes/utils";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { financeSchema } from "./validations";

const useStep3 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const { id, action } = useParams();
  const form = useForm<any>({
    defaultValues: {
      [YEARLY_DUE_DATE]: undefined,
      [MONTHLY_DUE_DATE]: undefined,
      [LEASE_DUE_DATE]: undefined,
      [CURRENCY]: "USD",
      [BANK_NAME]: undefined,
      [ROUTING_NUMBER]: undefined,
      [ACCOUNT_NUMBER]: undefined,
      [PHONE_1]: undefined,
      [PHONE_2]: undefined,
    },
    resolver: zodResolver(financeSchema),
  });
  const navigate = useNavigate();
  const isEditDetails = action === "edit-details";
  const isAdd = action == "add";
  const [monthlyDateKey, setMonthlyDateKey] = useState<number>(
    Math.random() * 2
  );

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(financeSchema);

  const {
    data: communityFinancialInfo,
    isLoading: isFinancialInfoLoading,
    isRefetching: isFinancialInfoRefetching,
  } = useGetCommunityFinancialInfo({
    community_id: id || "",
    queryParams: {
      enabled: !!(id && !isAdd),
    },
  });

  useEffect(() => {
    if (communityFinancialInfo) {
      form.reset({
        [YEARLY_DUE_DATE]:
          communityFinancialInfo?.data?.[YEARLY_DUE_DATE] || undefined,
        [MONTHLY_DUE_DATE]:
          communityFinancialInfo?.data?.[MONTHLY_DUE_DATE]?.toString() ||
          undefined,
        [LEASE_DUE_DATE]:
          communityFinancialInfo?.data?.[LEASE_DUE_DATE] || undefined,
        [CURRENCY]: communityFinancialInfo?.data?.[CURRENCY] || undefined,
        [BANK_NAME]: communityFinancialInfo?.data?.[BANK_NAME] || undefined,
        [ROUTING_NUMBER]:
          communityFinancialInfo?.data?.[ROUTING_NUMBER] || undefined,
        [ACCOUNT_NUMBER]:
          communityFinancialInfo?.data?.[ACCOUNT_NUMBER] || undefined,
        [PHONE_1]: communityFinancialInfo?.data?.[PHONE_1] || undefined,
        [PHONE_2]: communityFinancialInfo?.data?.[PHONE_2] || undefined,
      });
      setMonthlyDateKey(Math.random() * 2);
    }
  }, [communityFinancialInfo]);

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateFinancialInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (isEditDetails) {
          navigate(
            `/${routes.cam}/${routes.communities}/${id}/${routes.details}`
          );
        } else {
          onNextClick && onNextClick();
        }
        toast.success("Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (
      _data[ROUTING_NUMBER] &&
      _data[ROUTING_NUMBER]?.toString().length !== 9
    ) {
      form.setError(ROUTING_NUMBER, {
        message: "Routing Number must be 9 digits",
      });
      return;
    }

    if (_data[ACCOUNT_NUMBER] && _data[ACCOUNT_NUMBER]?.toString().length < 6) {
      form.setError(ACCOUNT_NUMBER, {
        message: "Account Number must be more than 6 digits",
      });
      return;
    }

    updateInfo({ id: id || "", data: _data });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isUpdatePending,
    isEditDetails,
    id,
    isFinancialInfoLoading,
    isFinancialInfoRefetching,
    monthlyDateKey,
  };
};

export default useStep3;
